/**
 * QboConnectionCenter — admin UI to connect/disconnect QuickBooks Online.
 *
 * Phase 1 (this file):
 *   - Status panel: configured / connected / company name / realm ID / scopes
 *   - "Copy Redirect URI" helper (admin must paste this into Intuit Developer
 *     console under their app → Keys & OAuth → Redirect URIs).
 *   - "Connect QuickBooks" button opens Intuit OAuth in a popup; we listen
 *     for postMessage from the callback page and refresh the status.
 *   - "Test connection" hits /companyinfo and shows the company name.
 *   - "Disconnect" wipes the token row.
 *
 * Lives at /admin?tab=integrations (new tab on the admin dashboard).
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  CheckCircle2, XCircle, AlertTriangle, RefreshCw, Copy, Plug, Loader2, Building2, Link2Off, Clock, ExternalLink,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { API } from "@/App";

// The outbound IP pool Emergent calls third-party APIs from — Intuit's
// production assessment asks for exactly this. Same pool in preview and prod.
const EGRESS_IPS = [
  "34.55.174.138", "34.41.192.254", "35.184.194.43", "34.9.233.22",
  "35.226.221.174", "35.226.93.127", "34.69.195.180", "130.211.210.175",
  "34.61.212.41", "34.55.129.30",
];

function formatDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }); }
  catch { return iso; }
}

function daysFromEpoch(seconds) {
  if (!seconds) return null;
  const remainingMs = seconds * 1000 - Date.now();
  const days = Math.floor(remainingMs / 86400000);
  return days;
}

export default function QboConnectionCenter({ token }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [testing, setTesting] = useState(false);
  const [companyInfo, setCompanyInfo] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/integrations/qbo/status`, { headers });
      setStatus(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load QBO status");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  // Listen for the postMessage from the OAuth callback page (in popup)
  useEffect(() => {
    const onMessage = (event) => {
      if (event.data?.type === "qbo-connected") {
        toast.success(`QuickBooks connected · realm ${event.data.realm_id}`);
        fetchStatus();
      } else if (event.data?.type === "qbo-connect-failed") {
        toast.error("QuickBooks connection failed — see popup for details");
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [fetchStatus]);

  const handleConnect = async () => {
    setConnecting(true);
    try {
      const res = await axios.get(`${API}/admin/integrations/qbo/auth-url`, { headers });
      const popup = window.open(res.data.auth_url, "qbo-oauth", "width=720,height=820,resizable=yes");
      if (!popup) {
        toast.error("Popup blocked — allow popups for this site and try again");
        return;
      }
      // Poll the popup; if user closes it, re-fetch status to detect any silent-success races
      const poll = setInterval(() => {
        if (popup.closed) {
          clearInterval(poll);
          fetchStatus();
        }
      }, 800);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start OAuth flow");
    } finally {
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm("Disconnect QuickBooks? You'll need to re-authorize to sync again.")) return;
    try {
      await axios.post(`${API}/admin/integrations/qbo/disconnect`, {}, { headers });
      toast.success("QuickBooks disconnected");
      setCompanyInfo(null);
      await fetchStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Disconnect failed");
    }
  };

  const handleTestConnection = async () => {
    setTesting(true);
    try {
      const res = await axios.get(`${API}/admin/integrations/qbo/company-info`, { headers });
      setCompanyInfo(res.data);
      toast.success(`Connection healthy — ${res.data.company_name || res.data.realm_id}`);
      await fetchStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Connection test failed");
    } finally {
      setTesting(false);
    }
  };

  const handleForceRefresh = async () => {
    setRefreshing(true);
    try {
      await axios.post(`${API}/admin/integrations/qbo/refresh`, {}, { headers });
      toast.success("Access token refreshed");
      await fetchStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Refresh failed");
    } finally {
      setRefreshing(false);
    }
  };

  const handleSwitchEnv = async (env) => {
    if (!window.confirm(env === "production"
      ? "Switch to your real QuickBooks company? The sandbox connection is dropped and you'll reconnect against the live books."
      : "Switch back to the sandbox company? The live connection is dropped.")) return;
    try {
      const r = await axios.post(`${API}/admin/integrations/qbo/environment`, { env }, { headers });
      toast.success(`Now pointing at ${env}`, { description: r.data?.message, duration: 10000 });
      setCompanyInfo(null);
      await fetchStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not switch environment", { duration: 12000 });
    }
  };

  const copy = (value, label = "Redirect URI") => {
    navigator.clipboard.writeText(value || "");
    toast.success(`${label} copied`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-slate-500" data-testid="qbo-loading">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading QuickBooks status…
      </div>
    );
  }

  if (!status?.configured) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-6" data-testid="qbo-not-configured">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-bold text-amber-900">QuickBooks credentials not configured</h3>
            <p className="text-sm text-amber-800 mt-1">
              Set <code className="bg-amber-100 px-1 rounded">QBO_CLIENT_ID</code>, <code className="bg-amber-100 px-1 rounded">QBO_CLIENT_SECRET</code>, and <code className="bg-amber-100 px-1 rounded">QBO_REDIRECT_URI</code> in <code className="bg-amber-100 px-1 rounded">backend/.env</code>, then restart the backend.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const connected = status.connected;
  const refreshDays = daysFromEpoch(status.refresh_expires_at);

  return (
    <div className="space-y-6" data-testid="qbo-connection-center">
      {/* Header strip */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
            <Plug className="w-5 h-5 text-emerald-600" /> QuickBooks Online
          </h2>
          <p className="text-xs text-slate-500 mt-0.5">
            Connection · environment: <span className="font-semibold uppercase">{status.env}</span>
            {status.webhooks_configured ? " · webhooks live" : " · 15-min polling"}
          </p>
        </div>
        <Badge className={connected ? "bg-emerald-100 text-emerald-800 border-emerald-200" : "bg-slate-100 text-slate-700 border-slate-200"}>
          {connected ? <CheckCircle2 className="w-3 h-3 mr-1" /> : <XCircle className="w-3 h-3 mr-1" />}
          {connected ? "Connected" : "Not connected"}
        </Badge>
      </div>

      {/* Step 1 — Redirect URI helper (only shown until connected, but kept visible because admins re-register URIs frequently) */}
      <div className="bg-slate-50 border border-slate-200 rounded-lg p-4" data-testid="qbo-redirect-uri-card">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0 flex-1">
            <h3 className="text-sm font-bold text-slate-900 mb-1">Step 1 · Register this redirect URI in your Intuit app</h3>
            <p className="text-xs text-slate-600">{status.needs_redirect_uri_registered}</p>
            <code className="block mt-2 text-xs bg-white border border-slate-200 rounded px-2 py-1.5 font-mono break-all" data-testid="qbo-redirect-uri">{status.redirect_uri}</code>
          </div>
          <div className="flex gap-2 flex-shrink-0">
            <Button size="sm" variant="outline" onClick={() => copy(status.redirect_uri)} data-testid="qbo-copy-redirect">
              <Copy className="w-3 h-3 mr-1" /> Copy
            </Button>
            <a href="https://developer.intuit.com/app/developer/myapps" target="_blank" rel="noreferrer">
              <Button size="sm" variant="outline">
                <ExternalLink className="w-3 h-3 mr-1" /> Intuit Console
              </Button>
            </a>
          </div>
        </div>
        <p className="text-[11px] text-slate-500 mt-2">
          Open your Intuit Developer app → <strong>Keys &amp; OAuth</strong> → <strong>Redirect URIs</strong> → paste the URL above → Save. Use the {status.env === "sandbox" ? "Development" : "Production"} keys section.
        </p>
      </div>

      {/* Step 2 — Connect / Connection details */}
      {!connected ? (
        <div className="bg-white border-2 border-dashed border-emerald-300 rounded-lg p-6 text-center" data-testid="qbo-connect-prompt">
          <Plug className="w-10 h-10 text-emerald-500 mx-auto mb-2" />
          <h3 className="text-base font-bold text-slate-900">Step 2 · Connect QuickBooks</h3>
          <p className="text-sm text-slate-600 mt-1 mb-4">A new tab will open. Sign in to Intuit and authorize askisaac for your QBO {status.env === "sandbox" ? "sandbox" : "company"}.</p>
          <Button onClick={handleConnect} disabled={connecting} className="bg-emerald-600 hover:bg-emerald-700" data-testid="qbo-connect-btn">
            {connecting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plug className="w-4 h-4 mr-2" />}
            Connect QuickBooks
          </Button>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid="qbo-connection-details">
            <h3 className="text-sm font-bold text-slate-900 mb-3 flex items-center gap-2">
              <Building2 className="w-4 h-4 text-emerald-600" /> Connection Details
            </h3>
            <dl className="text-xs space-y-2">
              <div className="flex justify-between gap-3"><dt className="text-slate-500">Company</dt><dd className="font-semibold text-slate-900 text-right">{status.company_name || companyInfo?.company_name || "—"}</dd></div>
              <div className="flex justify-between gap-3"><dt className="text-slate-500">Realm ID</dt><dd className="font-mono text-slate-900">{status.realm_id}</dd></div>
              <div className="flex justify-between gap-3"><dt className="text-slate-500">Connected</dt><dd className="text-slate-900">{formatDate(status.connected_at)}</dd></div>
              <div className="flex justify-between gap-3"><dt className="text-slate-500">Last token refresh</dt><dd className="text-slate-900">{formatDate(status.last_refreshed_at)}</dd></div>
              <div className="flex justify-between gap-3"><dt className="text-slate-500">Refresh token expires</dt><dd className={refreshDays != null && refreshDays < 14 ? "text-amber-700 font-semibold" : "text-slate-900"}>
                {refreshDays != null ? `in ${refreshDays} day${refreshDays === 1 ? "" : "s"}` : "—"}
              </dd></div>
              <div className="flex justify-between gap-3"><dt className="text-slate-500">Scopes</dt><dd className="font-mono text-[10px] text-slate-700 break-all text-right">{status.scopes || "—"}</dd></div>
            </dl>
          </div>

          <div className="bg-white border border-slate-200 rounded-lg p-4 flex flex-col" data-testid="qbo-actions-panel">
            <h3 className="text-sm font-bold text-slate-900 mb-3 flex items-center gap-2">
              <Clock className="w-4 h-4 text-cyan-600" /> Actions
            </h3>
            <div className="grid gap-2 flex-1">
              <Button onClick={handleTestConnection} disabled={testing} variant="outline" data-testid="qbo-test-btn">
                {testing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />} Test connection
              </Button>
              <Button onClick={handleForceRefresh} disabled={refreshing} variant="outline" data-testid="qbo-refresh-btn">
                {refreshing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />} Refresh access token
              </Button>
              <Button onClick={handleDisconnect} variant="outline" className="text-rose-600 hover:bg-rose-50 hover:text-rose-700 border-rose-200" data-testid="qbo-disconnect-btn">
                <Link2Off className="w-4 h-4 mr-2" /> Disconnect
              </Button>
            </div>
            {companyInfo && (
              <div className="mt-3 pt-3 border-t border-slate-100 text-xs text-emerald-700 bg-emerald-50 -mx-4 -mb-4 px-4 py-2 rounded-b-lg">
                ✓ Live: {companyInfo.company_name} · {companyInfo.country || "—"}
              </div>
            )}
          </div>
        </div>
      )}

      {/* What's next callout */}
      <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid="qbo-environment-card">
        <h3 className="text-sm font-bold text-slate-900 mb-1 flex items-center gap-2">
          <Plug className="w-4 h-4 text-slate-500" /> Which books are we on?
        </h3>
        <p className="text-xs text-slate-600">
          Currently <span className="font-bold uppercase">{status.env}</span>
          {status.using_prod_keys ? " (production keys)" : status.env === "production"
            ? " — but still using the sandbox keys, so add the production pair" : ""}.
          Going live takes one click; the stored connection is dropped because Intuit issues
          tokens per company, so you reconnect and authorize the real company.
        </p>
        <div className="flex gap-2 mt-3 flex-wrap">
          <Button size="sm" variant={status.env === "sandbox" ? "default" : "outline"}
            disabled={status.env === "sandbox"} onClick={() => handleSwitchEnv("sandbox")}
            data-testid="qbo-env-sandbox-btn">Use sandbox</Button>
          <Button size="sm" variant={status.env === "production" ? "default" : "outline"}
            disabled={status.env === "production"} onClick={() => handleSwitchEnv("production")}
            className={status.env === "production" ? "" : "border-emerald-300 text-emerald-700 hover:bg-emerald-50"}
            data-testid="qbo-env-production-btn">Go live on my real company</Button>
        </div>
        <p className="text-[11px] text-slate-500 mt-2">
          Production needs <code className="bg-slate-100 px-1 rounded">QBO_CLIENT_ID_PROD</code> and{" "}
          <code className="bg-slate-100 px-1 rounded">QBO_CLIENT_SECRET_PROD</code> in the backend
          environment — from developer.intuit.com → your app → Keys &amp; credentials (Production).
        </p>
        <div className="mt-3 pt-3 border-t border-slate-100">
          <p className="text-xs font-bold text-slate-900">Intuit asking for our server IP addresses?</p>
          <p className="text-[11px] text-slate-600 mt-0.5">
            Their production assessment wants the IPs our backend calls the QuickBooks API from.
            Paste all ten — both preview and the deployed app use this pool.
          </p>
          <code className="block mt-2 text-[11px] bg-slate-50 border border-slate-200 rounded px-2 py-1.5 font-mono whitespace-pre-line"
            data-testid="qbo-egress-ips">{EGRESS_IPS.join("\n")}</code>
          <Button size="sm" variant="outline" className="mt-2"
            onClick={() => copy(EGRESS_IPS.join("\n"), "Outbound IP list")}
            data-testid="qbo-copy-ips">
            <Copy className="w-3 h-3 mr-1" /> Copy all 10 IPs
          </Button>
        </div>
      </div>

      <div className={`rounded-lg p-4 border ${status.webhooks_configured
        ? "bg-emerald-50 border-emerald-200" : "bg-amber-50 border-amber-200"}`}
        data-testid="qbo-webhook-card">
        <h3 className="text-sm font-bold text-slate-900 mb-1 flex items-center gap-2">
          {status.webhooks_configured
            ? <CheckCircle2 className="w-4 h-4 text-emerald-600" />
            : <AlertTriangle className="w-4 h-4 text-amber-600" />}
          Real-time updates {status.webhooks_configured ? "are on" : "are not on yet"}
        </h3>
        <p className="text-xs text-slate-700">
          {status.webhooks_configured
            ? "QuickBooks pushes every change straight to us, and the 15-minute sync runs underneath as a safety net."
            : "Without the verifier token we poll QuickBooks every 15 minutes — still hands-free, just not instant."}
        </p>
        <code className="block mt-2 text-xs bg-white border border-slate-200 rounded px-2 py-1.5 font-mono break-all"
          data-testid="qbo-webhook-url">
          {(status.redirect_uri || "").replace("/api/integrations/qbo/callback", "")}/api/integrations/qbo/webhook
        </code>
        <div className="flex gap-2 mt-2">
          <Button size="sm" variant="outline"
            onClick={() => copy(`${(status.redirect_uri || "").replace("/api/integrations/qbo/callback", "")}/api/integrations/qbo/webhook`, "Webhook URL")}
            data-testid="qbo-copy-webhook">
            <Copy className="w-3 h-3 mr-1" /> Copy webhook URL
          </Button>
        </div>
        <p className="text-[11px] text-slate-600 mt-2">
          Intuit app → <strong>Webhooks</strong> → paste that URL → subscribe to <strong>Estimate,
          Invoice, PurchaseOrder, Payment, BillPayment, Customer, Vendor, Item</strong> → copy the
          generated <strong>Verifier Token</strong> into{" "}
          <code className="bg-white px-1 rounded">QBO_WEBHOOK_VERIFIER_TOKEN</code> and restart the backend.
        </p>
      </div>

      <div className="bg-slate-900 text-slate-200 rounded-lg p-4 text-xs" data-testid="qbo-roadmap">
        <p className="font-bold text-white mb-1">What unlocks once you&rsquo;re connected</p>
        <ul className="grid md:grid-cols-2 gap-x-4 gap-y-1 list-disc list-inside text-slate-400">
          <li><span className="text-slate-200">Live now:</span> QuickBooks estimates, invoices and POs become orders on the Sourcing Desk</li>
          <li><span className="text-slate-200">Live now:</span> payments recorded in QuickBooks mark the order paid here</li>
          <li><span className="text-slate-200">Next:</span> outbound — orders built here write themselves into QuickBooks</li>
          <li><span className="text-slate-200">Next:</span> edit in either place and have both sides agree</li>
        </ul>
      </div>
    </div>
  );
}
